import { filterContentBoxesForPresentDay } from "./contentBoxPresentQuery";
import { filterContentBoxesForPrintView } from "./printViewDateFilter";

export const isStableContentBoxOrderEnabled = () =>
  process.env.REACT_APP_STABLE_CONTENT_BOX_ORDER !== "false";

export const getContentBoxId = (box) => String(box?.contentBoxId ?? box?.id ?? "");

export const contentBoxIdsEqual = (leftIds, rightIds) => {
  if (leftIds === rightIds) return true;
  if (!Array.isArray(leftIds) || !Array.isArray(rightIds) || leftIds.length !== rightIds.length) {
    return false;
  }
  return leftIds.every((id, index) => id === rightIds[index]);
};

export const sortContentBoxesByClassOrderLegacy = (boxes, order) => {
  if (!Array.isArray(boxes)) return [];

  const normalizedOrder = Array.isArray(order) ? order : [];
  if (normalizedOrder.length === 0) return [...boxes];

  return [...boxes].sort((a, b) => {
    const aId = a.contentBoxId ?? a.id;
    const bId = b.contentBoxId ?? b.id;
    const indexA = normalizedOrder.indexOf(aId);
    const indexB = normalizedOrder.indexOf(bId);
    return (indexA !== -1 ? indexA : Infinity) - (indexB !== -1 ? indexB : Infinity);
  });
};

export const sortContentBoxesByClassOrderStable = (boxes, order) => {
  if (!Array.isArray(boxes) || boxes.length === 0) return [];

  const normalizedOrder = (Array.isArray(order) ? order : []).map((id) => String(id));
  const boxIds = boxes.map(getContentBoxId).filter(Boolean);
  const knownIdSet = new Set(normalizedOrder);
  const missingIds = [...new Set(boxIds.filter((id) => !knownIdSet.has(id)))].sort((a, b) =>
    a.localeCompare(b)
  );
  const effectiveOrder = [...normalizedOrder, ...missingIds];
  const indexById = new Map(effectiveOrder.map((id, index) => [id, index]));

  return [...boxes].sort((a, b) => {
    const aId = getContentBoxId(a);
    const bId = getContentBoxId(b);
    const indexA = indexById.has(aId) ? indexById.get(aId) : Number.MAX_SAFE_INTEGER;
    const indexB = indexById.has(bId) ? indexById.get(bId) : Number.MAX_SAFE_INTEGER;
    if (indexA !== indexB) return indexA - indexB;
    return aId.localeCompare(bId);
  });
};

export const sortContentBoxesByClassOrderCopyLegacy = (boxes, order) => {
  const normalizedOrder = (Array.isArray(order) ? order : []).map((id) => String(id));
  if (!Array.isArray(boxes) || boxes.length === 0) return [];
  if (normalizedOrder.length === 0) return [...boxes];

  const indexById = new Map(normalizedOrder.map((id, index) => [id, index]));
  return [...boxes].sort((a, b) => {
    const aId = getContentBoxId(a);
    const bId = getContentBoxId(b);
    const indexA = indexById.has(aId) ? indexById.get(aId) : Number.MAX_SAFE_INTEGER;
    const indexB = indexById.has(bId) ? indexById.get(bId) : Number.MAX_SAFE_INTEGER;
    if (indexA !== indexB) return indexA - indexB;
    return aId.localeCompare(bId);
  });
};

export const sortContentBoxesByClassOrder = (boxes, order) => {
  if (isStableContentBoxOrderEnabled()) {
    return sortContentBoxesByClassOrderStable(boxes, order);
  }
  return sortContentBoxesByClassOrderLegacy(boxes, order);
};

export const sortContentBoxesForCopy = (boxes, order) => {
  if (isStableContentBoxOrderEnabled()) {
    return sortContentBoxesByClassOrderStable(boxes, order);
  }
  return sortContentBoxesByClassOrderCopyLegacy(boxes, order);
};

export const getPresentDayContentBoxes = (
  boxes,
  { date, settings = {}, contentBoxesOrder = [], visibleOnly = false } = {}
) => {
  let filtered = filterContentBoxesForPresentDay(boxes, date, settings);
  filtered = sortContentBoxesByClassOrder(filtered, contentBoxesOrder);
  if (visibleOnly) {
    filtered = filtered.filter((box) => box?.show !== false);
  }
  return filtered;
};

export const getPrintViewContentBoxes = (
  boxes,
  { date, settings = {}, contentBoxesOrder = [], maxBoxes = 3 } = {}
) => {
  const useActiveDates = settings?.useActiveDates === true;

  if (!isStableContentBoxOrderEnabled()) {
    return filterContentBoxesForPrintView(boxes, date, useActiveDates)
      .filter((box) => box?.show !== false)
      .slice(0, maxBoxes);
  }

  return getPresentDayContentBoxes(boxes, {
    date,
    settings,
    contentBoxesOrder,
    visibleOnly: true,
  }).slice(0, maxBoxes);
};

export const boxesNeedUpdate = (prevBoxes, nextBoxes) => {
  if (!prevBoxes && !nextBoxes) return false;
  if (!prevBoxes || !nextBoxes) return true;
  if (prevBoxes.length !== nextBoxes.length) return true;

  for (let index = 0; index < prevBoxes.length; index += 1) {
    const prevId = getContentBoxId(prevBoxes[index]);
    const nextId = getContentBoxId(nextBoxes[index]);
    if (prevId !== nextId) return true;
    if (prevBoxes[index] !== nextBoxes[index]) return true;
  }

  return false;
};
